import conversions from '@sstdev/lib_epc-conversions';
import getGlobalConfig from './globalConfig';

export const _private = {
    getGlobalConfig
};
/**
 * @typedef {string} hex 0-9A-F
 * @typedef {import('../../types').STRATEGY_TYPES} TYPES
 */
export const STRATEGY_TYPES = {
    /**
     * @type {TYPES.ASCII}
     * @description Assume AssetNo is ASCII, and tagId is HEX representation of that
     */
    ASCII: 'ascii',
    /**
     * @type {number}
     * @description Max. Length for Short Value Encoding Strategy
     */
    ASCII_MAX_SHORT_LENGTH: 12,
    /**
     * @type {TYPES.HEX}
     * @description Assume AssetNo is HEX, and tagId will be the same
     */
    HEX: 'hex',
    /**
     * @type {TYPES.GS1}
     * @description GS1 TDS
     * @see https://www.gs1.org/standards/epc-rfid/tds
     */
    GS1: 'gs1',
    /**
     * @type {TYPES.GID}
     * @description GS1 GID
     * @see https://www.gs1.org/standards/epc-rfid/tds
     */
    GID: 'gid',
    /**
     * @type {TYPES._ID}
     * @description Use the _id field of the record
     */
    _ID: '_id',
    /**
     * @type {number}
     * @description Default max. length for short value encoding strategy
     */
    DEFAULT_MAX_SHORT_LENGTH: 24
};

const { ascii, gs1, supportedTypes: types } = conversions;

/**
 * Centralized helpers for trimming hex strings
 * Please use these helpers instead of direct regex operations to ensure consistency across the codebase
 */

/**
 * Trims leading and trailing null characters (hex `00`) from a string.
 * @param {hex} hex
 * @returns {hex}
 */
function trimAsciiNullsBothEnds(hex) {
    return typeof hex === 'string' ? hex.replace(/^(00)+/, '').replace(/(00)+$/, '') : hex;
}

/**
 * Trims leading zeros from a string.
 * @param {hex} hex
 * @returns {hex}
 */
function trimHexLeadingZeros(hex) {
    return typeof hex === 'string' ? hex.replace(/^0+/, '') : hex;
}

/**
 * True when every character of `str` is in the printable ASCII range
 * 0x20 (space) through 0x7E (~). Control characters (< 0x20, 0x7F) and
 * extended/Latin-1 bytes (>= 0x80, e.g. 0xFF -> "ÿ") are NOT printable ASCII.
 * An empty string is considered printable (a blank tag).
 * @param {string} str
 * @returns {boolean}
 */
export function isPrintableAscii(str) {
    return typeof str === 'string' && /^[\x20-\x7E]*$/.test(str);
}

/**
 * True when a stored hex tagId can be *unambiguously* decoded to a printable
 * ASCII string. We require both that the decoded characters are all printable
 * ASCII AND that re-encoding them reproduces the stored hex (ignoring ASCII
 * null padding). Without this, bytes like 0xFF decode to the Latin-1 char "ÿ",
 * which round-trips but is not meaningful ASCII the user ever typed.
 * @param {hex} tagId
 * @returns {boolean}
 */
export function canDisplayAsAscii(tagId) {
    if (typeof tagId !== 'string' || tagId === '') return false;
    const decoded = ascii.fromHex(tagId);
    if (!isPrintableAscii(decoded)) return false;
    const reEncoded = ascii.toHex(decoded).toUpperCase();
    const normalized = trimAsciiNullsBothEnds(tagId.toUpperCase());
    return reEncoded === normalized;
}

/**
 * True when a stored hex tagId is *confidently* a GS1/EPC encoding: it parses
 * to a known EPC type AND re-encodes (`.hex`) back to the same value. The
 * round-trip rejects coincidences where a non-GS1 value (e.g. the ASCII hex of
 * "12345678") happens to start with a recognized EPC header byte but gets
 * zero-padded to a different canonical hex on re-encode.
 * @param {hex} tagId
 * @returns {boolean}
 */
export function isLikelyGs1Hex(tagId) {
    if (typeof tagId !== 'string' || tagId.length <= 2) return false;
    try {
        const parsed = gs1.parse(tagId);
        return !!parsed && typeof parsed.hex === 'string' && parsed.hex.toUpperCase() === tagId.toUpperCase();
    } catch {
        return false;
    }
}

/**
 * Gets the tagId for a record, E.g. when a new asset is created without tagId
 * THis is used generate the tagId off of the other information, to store it in the database. e.g. in case of an import.
 * Then, when you PRINT that record to a label with RFID, then yes, that value would be encoded onto the tag.
 *
 * The easy scenario, think old "displayInAscii":
 * They import a record with AssetNo 123
 * Based on that feature flag, we'd (try to) match that up with a tagId value of 0x313233 (the hex value of 123)
 *
 * Now, imagine an asset that they gave an assetNo of 12345678901234567890
 * Previously (without these strategies) given that we had displayInAscii, we would convert that to hex, getting the following value:
 * 0x3132333435363738393031323334353637383930, which is 40 character long: too long to encode on an rfid tag.
 * Previously, we would not be able to print a tag for them using an automatically generated tagId value.
 *
 * Item Tracking works around this by allowing the user to enter some other value.
 * But for Asset Tracking, we want it to be more magical.
 *
 * With this strategy, the magic we use is that we generate "some" more than likely unique value that DOES fit on an rfid tag.
 * We then store that in mongo, and subsequently encode that on an rfid tag when printed, it all works fine.
 * Then, when that unique value is read by a fixed or handheld RFID reader, it gets looked up,
 * and totally matches what we have in the database, and the customer is happy.
 *
 * The 2 pieces of background knowledge required in this:
 *
 * 1) Ideally, we (David B) want RFID tag values (tagIds) that actually represent the assetNo if possible.
 *    This allows them to purchase externally pre-encoded labels without much extra communication.
 * 2) If it is not possible, it's not possible. In that case we need to have "some" strategy
 *    to generate a unique tagId in the case they print from our system.
 *    Alternatively, they can still "Associate" any tag, which will then ignore and override the value we generated (it simply goes unused).
 *
 * Obviously, the _id on a record is unique by nature of being an ObjectID. Replacing the first 2 characters does introduce a small but extremely unlikely chance to turn it into a duplicate, but is necessary to be able to detect the encoding strategy used. (0x1A does NOT translate to a printable ascii character, and hence can be used to differentiate from any ascii-conversion based hex value.)
 * @param {string|object} record
 * @param {string} [titularField='title']
 * @returns {string} hex value to be used as the tagId
 */
export function getTagId(record, titularField = 'title') {
    let value;
    if (typeof record === 'string') {
        value = record;
    } else {
        if (record.tagId) return record.tagId;
        value = record[titularField] || '';
    }
    if (!value) return '';
    const { maxShortLength, shortStrategy, longStrategy } = getRfidConfig();
    if (value.length <= maxShortLength) {
        return convertToHex(value, shortStrategy, record, maxShortLength);
    }
    return convertToHex(value, longStrategy, record, maxShortLength);
}

/**
 * Convert a value to hex based on the strategy
 * @param {string} value
 * @param {rfidConfig['shortStrategy'] | rfidConfig['longStrategy']} strategy
 * @param {object} record
 * @param {number} maxShortLength
 * @returns {string}
 */
function convertToHex(value, strategy, record, maxShortLength) {
    switch (strategy) {
        case STRATEGY_TYPES.ASCII:
            return ascii.toHex(value);
        case STRATEGY_TYPES._ID:
            value = record?._id?.substring(2) || value;
            if (value.length <= maxShortLength) {
                return value.toUpperCase();
            }
            // if the value is too long, we need to truncate it and add the 1A prefix (invisible character)
            // 0x1A does NOT translate to a printable ascii character, so it can be used to differentiate from any ascii-conversion based hex value.
            return `1A${value}`.toUpperCase();
        case STRATEGY_TYPES.GS1:
        case STRATEGY_TYPES.GID: {
            const gs1TdsValue = gs1.parse(value);
            return gs1TdsValue?.hex || value;
        }
        case STRATEGY_TYPES.HEX:
        default:
            return normalizeTagIdHex(value.toUpperCase(), { forGeneration: true });
    }
}

/**
 * Gets the value to be displayed for a tagId
 * used in EncodedShortText component
 * @param {hex} tagId in hex
 * @returns {string} the value to be displayed instead of the tagId
 */
export function getDisplayValue(tagId) {
    const { displayStrategy } = getRfidConfig();
    if (!tagId) return '';
    if (displayStrategy === STRATEGY_TYPES.ASCII) {
        // Only decode when the stored hex is unambiguously printable ASCII.
        // Otherwise show the raw hex rather than silently reinterpreting bytes
        // such as 0xFF as the Latin-1 character "ÿ".
        return canDisplayAsAscii(tagId) ? ascii.fromHex(tagId) : tagId;
    }

    if ([STRATEGY_TYPES.GS1, STRATEGY_TYPES.GID].includes(displayStrategy)) {
        // Only render as GS1 when the value confidently round-trips as a GS1/EPC
        // tag; otherwise show the raw hex instead of a coincidental parse. Parse
        // once and reuse for both the round-trip check and the display string.
        try {
            const gs1TdsValue = gs1.parse(tagId);
            if (gs1TdsValue?.hex?.toUpperCase() !== tagId.toUpperCase()) return tagId;
            return gs1TdsValue.toString() || tagId;
        } catch {
            return tagId; // if parsing fails, just return the tagId
        }
    }

    return tagId;
}
/**
 * Try to guess the titular (assetNo/title) value based on the tagId
 * This is NOT the same as the display value, as the display value is used to display the tagId field,
 * whereas the titular value is used to display (a guess towards) what the title
 * or assetNo value might have been intended to be.
 * e.g. in the case of auto add, or when displaying an unknown tag
 * @param {hex} tagId
 * @returns
 */
export function guessTitularValue(tagId) {
    const { shortStrategy, longStrategy } = getRfidConfig();
    if ([shortStrategy, longStrategy].includes(STRATEGY_TYPES._ID) && tagId.startsWith('1A')) {
        // nothing we can do, just return the value
        return tagId;
    }

    if ([shortStrategy, longStrategy].includes(STRATEGY_TYPES.ASCII)) {
        // Only decode to ASCII when the stored hex is unambiguously printable
        // ASCII. The old /[a-zA-Z0-9]/ heuristic let through values like 0x96/0x14
        // mixed with a single alphanumeric byte, surfacing gibberish (e.g. "6S██s")
        // in the unknown-tag list. Otherwise fall back to the raw (null-trimmed) hex
        // so the guessed title matches the raw hex shown elsewhere.
        if (canDisplayAsAscii(tagId)) {
            return ascii.fromHex(tagId);
        }
        return trimAsciiNullsBothEnds(tagId);
    }

    if (
        [shortStrategy, longStrategy].includes(STRATEGY_TYPES.GS1) ||
        [shortStrategy, longStrategy].includes(STRATEGY_TYPES.GID)
    ) {
        const gs1TdsValue = gs1.parse(tagId);
        if (gs1TdsValue.toTitle) {
            return gs1TdsValue.toTitle();
        }
        if ([types.gid96.id, types.sgtin96.id, types.sgtin198.id].includes(gs1TdsValue.type)) {
            return gs1TdsValue.serial;
        }

        // todo: GS1 TDS will generally contain multiple fields.
        // this will need further mapping!
        return tagId;
    }
    return normalizeTagIdHex(tagId);
}
/**
 * Given a tagId, returns a filter object to be used in a search query
 * to find the corresponding record
 * @param {string} tagId in hex
 * @param {string} [titularField='title']
 * @param {boolean} [simpleSearch = true] whether to return a search filter for just tagId, or also include the titularField
 * @returns {{tagId:string}|{$or:[object]}} a filter object to be used in a search query
 */
export function getFilter(tagId, titularField = 'title', simpleSearch = true) {
    const { shortStrategy, longStrategy } = getRfidConfig();
    if ([shortStrategy, longStrategy].includes(STRATEGY_TYPES._ID) && tagId.startsWith('1A')) {
        if (simpleSearch) {
            return { tagId };
        }
        return { $or: [{ _id: { $regex: new RegExp(`^..${tagId.slice(2)}$`) } }, { tagId }] };
    }

    if ([shortStrategy, longStrategy].includes(STRATEGY_TYPES.ASCII)) {
        // ascii: trim leading and trailing 00s
        const trimmedValue = trimAsciiNullsBothEnds(tagId);
        if (simpleSearch) {
            return { tagId: trimmedValue };
        }

        const asciiString = ascii.fromHex(tagId);
        if (!/[a-zA-Z0-9]/.test(asciiString)) {
            return { tagId: trimmedValue };
        }
        return { $or: [{ [titularField]: ascii.fromHex(tagId) }, { tagId: trimmedValue }] };
    }

    if (
        [shortStrategy, longStrategy].includes(STRATEGY_TYPES.GS1) ||
        [shortStrategy, longStrategy].includes(STRATEGY_TYPES.GID)
    ) {
        if (simpleSearch) {
            return { tagId };
        }
        const gs1TdsValue = gs1.parse(tagId);
        if (gs1TdsValue.type === types.gid96.id) {
            return { $or: [{ [titularField]: gs1TdsValue.serial?.toString() || '' }, { tagId }] };
        }

        // todo: GS1 TDS will generally contain multiple fields.
        // this will need further mapping!
        return { tagId };
    }
    const processedValue = normalizeTagIdHex(tagId);
    if (simpleSearch) {
        return { tagId: processedValue };
    }
    return { $or: [{ [titularField]: processedValue }, { tagId: processedValue }] };
}

/**
 * @typedef {object} rfidConfig
 * @property {number} maxShortLength
 * @property {string} shortStrategy
 * @property {string} longStrategy
 * @property {string} displayStrategy
 * @property {boolean} [stripPrefixFromTag]
 */
/**
 * Helper function to read settings with precedence: tenant settings > featureFlags
 * @param {string} settingName - The setting name to read
 * @param {object} tenantSettings - The tenant settings object
 * @param {object} config - The base RFID config object
 * @param {string[]} featureFlags - Array of feature flags
 * @param {boolean} [defaultValue=false] - Default value if not found in config or feature flags
 * @returns {boolean|undefined} The setting value with precedence applied
 */
function readWithPrecedence(settingName, tenantSettings, config, featureFlags, defaultValue = false) {
    // Priority: tenant settings > config > featureFlags > defaultValue
    if (tenantSettings?.[settingName] !== undefined) {
        return tenantSettings[settingName];
    }
    if (config?.[settingName] !== undefined) {
        return config[settingName];
    }
    if (featureFlags.includes(settingName)) {
        return true;
    }
    return defaultValue;
}

/**
 * Centralized RFID configuration and settings inferred from feature flags and tenant settings
 * @returns {rfidConfig & { normalizedSettings: { stripPrefixFromTag: boolean, strip0s: boolean, displayInAscii: boolean, useGs1: boolean, RFID_PRINTING: boolean|undefined } }}
 */
export function getRfidConfig() {
    const globalConfig = _private.getGlobalConfig();
    const baseConfig = globalConfig.rfid || {};
    const tenantSettings = globalConfig.tenantSettings || {};
    const featureFlags = globalConfig.featureFlags || [];

    // 1. Establish defaults based on feature flags
    let defaults = {};
    const isAsciiModeEnabled = featureFlags.includes('displayInAscii');
    if (isAsciiModeEnabled) {
        defaults = {
            shortStrategy: STRATEGY_TYPES.ASCII,
            longStrategy: STRATEGY_TYPES._ID,
            maxShortLength: STRATEGY_TYPES.ASCII_MAX_SHORT_LENGTH,
            displayStrategy: STRATEGY_TYPES.ASCII
        };
    } else {
        defaults = {
            shortStrategy: STRATEGY_TYPES.HEX,
            longStrategy: STRATEGY_TYPES._ID,
            maxShortLength: STRATEGY_TYPES.DEFAULT_MAX_SHORT_LENGTH,
            displayStrategy: STRATEGY_TYPES.HEX
        };
    }

    // 2. Merge with tenant config (tenant overrides defaults)
    let mergedConfig = {
        ...defaults,
        ...baseConfig
    };
    
    // 3. Update a stale HEX display strategy when ASCII mode is active without
    // overriding an explicitly configured GS1 or GID display strategy.
    if (isAsciiModeEnabled && mergedConfig.displayStrategy === STRATEGY_TYPES.HEX) {
        mergedConfig.displayStrategy = STRATEGY_TYPES.ASCII;
    }

    // 4. Normalize tenant settings with precedence
    // Priority: tenant settings > featureFlags > defaults
    const normalizedSettings = {
        stripPrefixFromTag: readWithPrecedence('stripPrefixFromTag', tenantSettings, baseConfig, featureFlags, true), // default to true
        strip0s: readWithPrecedence('strip0s', tenantSettings, baseConfig, featureFlags),
        displayInAscii: readWithPrecedence('displayInAscii', tenantSettings, baseConfig, featureFlags),
        useGs1: readWithPrecedence('useGs1', tenantSettings, baseConfig, featureFlags),
        RFID_PRINTING: tenantSettings.RFID_PRINTING ?? baseConfig.RFID_PRINTING
    };

    // 5. Return enhanced config with normalized settings
    return {
        ...mergedConfig,
        normalizedSettings
    };
}

/**
 * Compute zero-trimming policy from enhanced getRfidConfig() with full precedence support
 * @returns {{ displayInAscii: boolean, useGs1: boolean, stripPrefixFromTag: boolean, strip0sEnabled: boolean, effectiveStrip0s: boolean }}
 */
export function computeZeroTrimPolicyFromConfig() {
    const config = getRfidConfig();
    const settings = config.normalizedSettings;

    // Derive displayInAscii from strategy OR tenant setting
    const displayInAscii = config.displayStrategy === STRATEGY_TYPES.ASCII || settings.displayInAscii;

    // Derive useGs1 from strategies OR tenant setting
    const useGs1 =
        [config.shortStrategy, config.longStrategy, config.displayStrategy].some(strategy =>
            [STRATEGY_TYPES.GS1, STRATEGY_TYPES.GID].includes(strategy)
        ) || settings.useGs1;

    const stripPrefixFromTag = settings.stripPrefixFromTag; // defaults to true unless explicitly disabled
    const strip0sEnabled = settings.strip0s;

    // effectiveStrip0s when:
    // - strip0s is enabled
    // - NOT GS1
    // - and stripPrefixFromTag is enabled
    // This allows trimming zeros when no prefix exists without requiring stripPrefixFromTag
    const effectiveStrip0s = !!strip0sEnabled && !useGs1 && !!stripPrefixFromTag;

    return {
        displayInAscii: !!displayInAscii,
        useGs1: !!useGs1,
        stripPrefixFromTag: !!stripPrefixFromTag,
        strip0sEnabled: !!strip0sEnabled,
        effectiveStrip0s
    };
}

/**
 * Centralized tag hex processing with consistent policy application
 * @param {string} tagHex - The hex string to process
 * @param {object} [options] - Processing options
 * @param {boolean} [options.forGeneration=false] - Whether this is for tag generation vs normalization
 * @param {object} [options.policy] - Pre-computed policy (for performance)
 * @returns {string} Processed hex string
 */
function normalizeTagIdHex(tagHex, options = {}) {
    if (typeof tagHex !== 'string') return tagHex;

    // Use provided policy or compute once
    const policy = options.policy || computeZeroTrimPolicyFromConfig();

    // Apply consistent normalization logic
    if (policy.displayInAscii) {
        return trimAsciiNullsBothEnds(tagHex);
    }
    if (policy.effectiveStrip0s) {
        return trimHexLeadingZeros(tagHex);
    }
    return tagHex;
}

export default {
    getTagId,
    guessTitularValue,
    getDisplayValue,
    getFilter,
    getRfidConfig,
    STRATEGY_TYPES,
    computeZeroTrimPolicyFromConfig,
    normalizeTagIdHex,
    isPrintableAscii,
    canDisplayAsAscii,
    isLikelyGs1Hex
};
